import React from 'react';
import { FlashtoolsMeterKey, FlashtoolsMeterRate, FlashtoolsMeterRates } from '../types';

// Shared metered-usage copy and tile, used by both the Profile usage section and
// the Billing page so a meter is described the same way everywhere.
//
// Three shapes a meter can take on a plan, driven by `metered_rates` from the
// API rather than guessed from `limits`:
//   included allowance + rate → progress bar, rate applies past the allowance
//   rate with no allowance     → pay-per-use, every unit is billed
//   no rate                    → included with the plan, never billed per-unit

export const METER_LABELS: Record<FlashtoolsMeterKey, string> = {
  waitlist_signup: 'Waitlist signups',
  ticket_purchase: 'Tickets purchased by Flash Buy',
};

/**
 * Canonical display order. The API returns metered rates keyed off a set, so
 * ordering is arbitrary there — sort against this instead.
 */
export const METER_ORDER = Object.keys(METER_LABELS) as FlashtoolsMeterKey[];

/** Canonical order, deduped, limited to the keys asked for. */
export function sortMeterKeys(keys: FlashtoolsMeterKey[]) {
  return METER_ORDER.filter(k => keys.indexOf(k) !== -1);
}

/** Singular unit noun for rate copy — "$2.00 per ticket". */
const METER_UNITS: Record<FlashtoolsMeterKey, { one: string; many: string }> = {
  waitlist_signup: { one: 'signup', many: 'signups' },
  ticket_purchase: { one: 'ticket', many: 'tickets' },
};

export function formatUsd(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export function meterUnit(key: FlashtoolsMeterKey, count: number) {
  const unit = METER_UNITS[key];
  return count === 1 ? unit.one : unit.many;
}

/**
 * How much each billed unit costs, as plain text. "additional" only when
 * there's an allowance to exceed first — a pay-per-use meter charges from unit
 * one, so calling it "additional" would imply a free tier that doesn't exist.
 *
 * Prefer `<MeterRateCopy>` for anything a customer reads: it renders the same
 * sentence with the money emphasised. This stays for aria-labels, titles and
 * anywhere a string is required.
 */
export function meterRateCopy(key: FlashtoolsMeterKey, rate: FlashtoolsMeterRate) {
  const unit = METER_UNITS[key].one;
  return rate.included > 0
    ? `${formatUsd(rate.unit_cents)} per additional ${unit}`
    : `${formatUsd(rate.unit_cents)} per ${unit}`;
}

/**
 * A money figure that must not blend into the sentence around it.
 *
 * Sellers were reading "then $0.10 per additional signup" as part of the plan
 * and only meeting the charge on an invoice — same for Reserve's $2.00 per
 * ticket. Per-unit costs are the one number on these surfaces that can grow
 * without warning, so they are set apart everywhere they appear: amber, one
 * weight heavier than the copy carrying them. Amber, not red — this is a cost
 * to notice, not an error — and it is the colour `UsageMeter` already uses for
 * overage.
 *
 * Use this for EVERY per-unit rate shown to a customer. A rate rendered in the
 * surrounding text colour is the bug this exists to prevent.
 */
export const MeterRate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <strong className="font-semibold text-amber-300">{children}</strong>
);

/** `meterRateCopy` with the price emphasised — the customer-facing form. */
export const MeterRateCopy: React.FC<{
  meterKey: FlashtoolsMeterKey;
  rate: FlashtoolsMeterRate;
}> = ({ meterKey, rate }) => (
  <>
    <MeterRate>{formatUsd(rate.unit_cents)}</MeterRate>
    {rate.included > 0 ? ' per additional ' : ' per '}
    {METER_UNITS[meterKey].one}
  </>
);

interface UsageMeterProps {
  meterKey: FlashtoolsMeterKey;
  used: number;
  /** Allowance included in the plan this period. 0 = none. */
  included: number;
  /** Per-unit rate, or undefined when this meter isn't billed on the plan. */
  rate?: FlashtoolsMeterRate;
  /**
   * Hard cap in force only while trialing. Carries its own `used` count — the
   * server tallies capped actions directly rather than from billing usage
   * events, which stay empty on tiers with no metered price.
   */
  trialCap?: { cap: number; used: number; remaining: number };
}

export function UsageMeter({ meterKey, used, included, rate, trialCap }: UsageMeterProps) {
  const label = METER_LABELS[meterKey];
  const payPerUse = !!rate && included === 0;
  const overage = Math.max(0, used - included);

  // A trial cap blocks rather than bills, so it outranks any rate copy: it's
  // the limit the user will actually hit first.
  if (trialCap && trialCap.cap > 0) {
    const pct = Math.min(100, (trialCap.used / trialCap.cap) * 100);
    const maxed = trialCap.remaining <= 0;
    return (
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-white">{label}</span>
          <span className="text-sm text-white/70">
            {trialCap.used} / {trialCap.cap}
          </span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className={`h-full ${maxed ? 'bg-red-400' : 'bg-blue-400'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className={`text-xs mt-1 ${maxed ? 'text-red-400' : 'text-white/40'}`}>
          {maxed
            ? 'Free-trial limit reached — upgrade to keep going'
            : `Free-trial limit · ${trialCap.remaining} left`}
        </p>
      </div>
    );
  }

  if (payPerUse) {
    // No allowance means no progress bar to draw — show the running charge
    // instead, which is the number the user actually cares about.
    const charge = used * rate!.unit_cents;
    return (
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-white">{label}</span>
          <span className="text-sm text-white/70">
            {used} {meterUnit(meterKey, used)}
          </span>
        </div>
        {used > 0 ? (
          <p className="text-xs text-white/60 mt-2">
            <MeterRate>{formatUsd(rate!.unit_cents)}</MeterRate> each ·{' '}
            <span className="text-white/80">{formatUsd(charge)} this period</span>
          </p>
        ) : (
          <p className="text-xs text-white/40 mt-2">
            Billed at <MeterRateCopy meterKey={meterKey} rate={rate!} />
          </p>
        )}
      </div>
    );
  }

  if (included <= 0) {
    // Unlimited and unbilled — quoting a rate here would invent a charge.
    return (
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-white">{label}</span>
          <span className="text-sm text-white/70">{used}</span>
        </div>
        <p className="text-xs text-white/40 mt-2">Included with your plan</p>
      </div>
    );
  }

  const pct = Math.min(100, (used / included) * 100);
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4">
      <div className="flex justify-between items-baseline">
        <span className="text-sm text-white">{label}</span>
        <span className="text-sm text-white/70">
          {used} / {included}
        </span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className={`h-full ${overage > 0 ? 'bg-amber-400' : 'bg-[#3b82f6]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {rate ? (
        overage > 0 ? (
          <p className="text-xs text-amber-400 mt-1">
            {overage} over included — billed at <MeterRateCopy meterKey={meterKey} rate={rate} />
          </p>
        ) : (
          <p className="text-xs text-white/40 mt-1">
            After included: <MeterRateCopy meterKey={meterKey} rate={rate} />
          </p>
        )
      ) : (
        overage > 0 && (
          <p className="text-xs text-amber-400 mt-1">{overage} over included</p>
        )
      )}
    </div>
  );
}

/**
 * Which meters to show for a plan: anything with an allowance, anything billed
 * per-unit, and anything already used this period.
 */
export function visibleMeterKeys(
  limits: Partial<Record<FlashtoolsMeterKey, number>>,
  usage: Partial<Record<FlashtoolsMeterKey, number>>,
  rates: FlashtoolsMeterRates = {},
  trialCaps: Partial<Record<FlashtoolsMeterKey, { cap: number }>> = {},
): FlashtoolsMeterKey[] {
  const keys = [
    ...(Object.keys(limits) as FlashtoolsMeterKey[]),
    ...(Object.keys(rates) as FlashtoolsMeterKey[]),
    ...(Object.keys(trialCaps) as FlashtoolsMeterKey[]),
  ];
  return sortMeterKeys(keys).filter(
    k =>
      (limits[k] ?? 0) > 0 ||
      (usage[k] ?? 0) > 0 ||
      !!rates[k] ||
      ((trialCaps[k]?.cap ?? 0) > 0),
  );
}
